import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { cva } from "class-variance-authority"
import { cn } from "../utils"
import {
  buttonBaseConfig,
  resolveButtonSize,
  useButtonTelemetry,
  type ButtonTelemetryProps,
  type ButtonVariantProps
} from "../button-base"

// =============================================================================
// Props
// =============================================================================
export type LinkButtonProps = {
  href: string
  /**
   * Replace the rendered element so consumers can supply their own link
   * component (e.g. Inertia `Link`, react-router `Link`, plain `<a>`).
   * Defaults to a `<a>` when omitted.
   *
   * The link text must be passed as `children` of the link button, NOT as children of
   * the render element — pass a childless element (`render={<Link href="…" />}`)
   * and let the link button compose the content. Children placed directly on the
   * render element (`render={<Link href="…">Forms</Link>}`) take precedence over
   * the link button's composed content.
   */
  render?: useRender.RenderProp
} & React.AnchorHTMLAttributes<HTMLAnchorElement> &
  ButtonVariantProps &
  ButtonTelemetryProps

// =============================================================================
// Styles
// =============================================================================
export const linkButtonStyles = cva(buttonBaseConfig.base, {
  ...buttonBaseConfig,
  variants: {
    ...buttonBaseConfig.variants,
    variant: {
      ...buttonBaseConfig.variants.variant
    }
  }
})

// =============================================================================
// Component
// =============================================================================
export const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  function LinkButton(
    {
      className,
      href,
      variant = "primary",
      render,
      size,
      telemetry,
      children,
      onClick,
      ...props
    },
    ref
  ) {
    const handleClick = useButtonTelemetry({ telemetry, children, onClick })

    return useRender({
      defaultTagName: "a",
      render,
      ref,
      props: {
        className: cn(
          linkButtonStyles({
            variant,
            size: resolveButtonSize(variant, size),
            className
          })
        ),
        href,
        "data-slot": "link-button",
        onClick: handleClick,
        children,
        ...props
      }
    })
  }
)

LinkButton.displayName = "LinkButton"
